import React from 'react'
import classNames from 'classnames'

import { textVariantsToBaseClasses } from './classes'
import type { BaseTextProps } from './types'

type BaseCustomTextProps = BaseTextProps &
  React.HTMLAttributes<HTMLDivElement> & {
    ref?: React.Ref<HTMLElement>
  }

type BaseHTMLDivTextProps = React.HTMLAttributes<HTMLDivElement> &
  Omit<BaseCustomTextProps, 'ref'> & {
    ref?: React.Ref<HTMLDivElement>
  }

type BaseLinkTextProps = {
  href: string
} & React.AnchorHTMLAttributes<HTMLAnchorElement> &
  Omit<BaseCustomTextProps, 'ref'> & {
    ref?: React.Ref<HTMLAnchorElement>
  }

type BaseHTMLLabelProps = {
  htmlFor: string
} & React.AnchorHTMLAttributes<HTMLLabelElement> &
  Omit<BaseCustomTextProps, 'ref'> & {
    ref?: React.Ref<HTMLLabelElement>
  }

const isCustomProps = (props: unknown): props is BaseCustomTextProps => {
  return Boolean((props as BaseCustomTextProps).as)
}

const isLinkProps = (props: unknown): props is BaseLinkTextProps => {
  return Boolean((props as BaseLinkTextProps).href)
}

const isLabelProps = (props: unknown): props is BaseHTMLLabelProps => {
  return Boolean((props as BaseHTMLLabelProps).htmlFor)
}

export type TextProps =
  | BaseCustomTextProps
  | BaseHTMLDivTextProps
  | BaseLinkTextProps
  | BaseHTMLLabelProps

export const Text = ({
  children,
  className,
  variant = 'body',
  color,
  ...props
}: TextProps) => {
  const classes = classNames(
    textVariantsToBaseClasses[variant].base,
    color ? color : textVariantsToBaseClasses[variant].defaultColor,
    className
  )

  if (isCustomProps(props)) {
    const { as = 'div', ref, ...componentProps } = props
    return React.createElement(
      as,
      { ...componentProps, className: classes, ref },
      children
    )
  }

  if (isLabelProps(props)) {
    return (
      <label className={classes} {...props}>
        {children}
      </label>
    )
  }

  if (isLinkProps(props)) {
    return (
      <a className={classes} {...props}>
        {children}
      </a>
    )
  }

  return (
    <div className={classes} {...(props as BaseHTMLDivTextProps)}>
      {children}
    </div>
  )
}
